import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/auth'
import { useCapabilitiesStore } from '@/store/capabilities'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor — add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Prevent multiple concurrent refresh attempts
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
}> = []

function shouldAttemptRefresh(
  error: AxiosError,
  originalRequest: (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined
): boolean {
  if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
    return false
  }

  const requestUrl = String(originalRequest.url || '')
  const refreshToken = useAuthStore.getState().refreshToken

  // Login/refresh/logout failures must surface to the page that initiated them.
  // Otherwise the interceptor reloads /login and erases the actual error state.
  if (
    requestUrl.includes('/auth/password-login') ||
    requestUrl.includes('/auth/refresh') ||
    requestUrl.includes('/auth/logout')
  ) {
    return false
  }

  // Anonymous requests do not have a refresh path.
  if (!refreshToken) {
    return false
  }

  return true
}

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(null)
    }
  })
  failedQueue = []
}

// Response interceptor — handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }
    const isAdminScreen = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')

    if (error.response?.status === 403 && isAdminScreen) {
      useCapabilitiesStore.getState().resetCapabilities()
      window.location.href = '/'
      return Promise.reject(error)
    }

    if (shouldAttemptRefresh(error, originalRequest)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            const newToken = useAuthStore.getState().token
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
            }
            return apiClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        await useAuthStore.getState().refreshAccessToken()
        const newToken = useAuthStore.getState().token
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }
        processQueue(null)
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as Error)
        // Clear local state without calling Zitadel logout API
        useAuthStore.getState().clearAuth()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient
